import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  FormControl,
  Typography,
  Box,
  Alert,
  Divider,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useGraphEditorStore } from '@/graph-store';
import { getLinkCandidates, isRootMaterial, isLeafMaterial } from '@/graph/domain/topology';
import { useToast } from '@/hooks/useToast'; // Add toast hook
import type { MaterialNodeSpec } from '@/graph/domain/types';

export interface LinkMaterialModalProps {
  /** ID of the material being linked (source endpoint) */
  sourceMaterialId: string;
  onClose: () => void;
  onLinked?: (targetMaterialId: string) => void;
}

/**
 * Modal for selecting a target material to link to.
 *
 * Shows candidates filtered by:
 * - Different connected component
 * - Opposite endpoint type (root ↔ leaf)
 * - Not already linked
 *
 * Displays material name and component context for clarity.
 */
export function LinkMaterialModal({
  sourceMaterialId,
  onClose,
  onLinked,
}: LinkMaterialModalProps) {
  const graphData = useGraphEditorStore((s) => s.graphData);
  const linkMaterialBranches = useGraphEditorStore(
    (s) => (s as any).linkMaterialBranches,
  );
  const [selectedTargetId, setSelectedTargetId] = React.useState<string>('');
  const { showError } = useToast();

  // Get source material info
  const sourceMaterial = graphData.materialNodes[sourceMaterialId];
  const isSourceRoot = isRootMaterial(sourceMaterialId, graphData.flowEdges);

  // Get link candidates
  const candidates = useMemo(() => {
    return getLinkCandidates(sourceMaterialId, graphData);
  }, [sourceMaterialId, graphData]);

  const handleConfirm = async () => {
    if (!selectedTargetId) return;

    try {
      if (linkMaterialBranches) {
        await linkMaterialBranches(sourceMaterialId, selectedTargetId);
        onLinked?.(selectedTargetId);
      }
      onClose();
    } catch (error) {
      // Show error toast with appropriate message based on error code
      let errorMessage = 'Failed to link materials';
      
      if (error instanceof Error) {
        if (error.message.includes('WOULD_CREATE_CYCLE')) {
          errorMessage = 'Creating this link would form a cycle';
        } else if (error.message.includes('ALREADY_LINKED')) {
          errorMessage = 'These materials are already linked';
        } else if (error.message.includes('SOURCE_NOT_FOUND')) {
          errorMessage = 'Source material not found';
        } else if (error.message.includes('TARGET_NOT_FOUND')) {
          errorMessage = 'Target material not found';
        } else if (error.message.includes('INVALID_ENDPOINT_PAIR')) {
          errorMessage = 'Invalid endpoint types for linking';
        } else if (error.message.includes('SAME_COMPONENT')) {
          errorMessage = 'Cannot link materials in the same connected component';
        } else {
          errorMessage = error.message || errorMessage;
        }
      }
      
      showError(errorMessage);
      console.error('Failed to link materials:', error);
    }
  };

  if (!sourceMaterial) {
    return (
      <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Link Material</DialogTitle>
        <DialogContent>
          <Alert severity="error">Source material not found</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: 1.5, overflow: 'hidden' } } }}
    >
      {/* Header band: light tinted background, bold primary-colored title, plain close icon */}
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'primary.50',
          py: 1.5,
          px: 3,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'black', fontSize: '1.1rem' }}>
          Choose Node to Link
        </Typography>
        <IconButton onClick={onClose} size="small" edge="end" sx={{ color: 'text.primary' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ px: 3, py: 3 }}>
        {/* Compact source context line */}
        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
          Linking <strong>{sourceMaterial.name}</strong> ·{' '}
          {isSourceRoot ? 'Root (no incoming edges)' : 'Leaf (no outgoing edges)'}
        </Typography>

        {candidates.length === 0 ? (
          <Alert severity="info">
            No available materials to link. Ensure there are disconnected branches with
            opposite endpoint types (root ↔ leaf).
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1" sx={{ whiteSpace: 'nowrap', color: 'text.primary' }}>
              Choose a node
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                id="target-select"
                displayEmpty
                value={selectedTargetId}
                onChange={(e) => setSelectedTargetId(e.target.value)}
                renderValue={(value) =>
                  value
                    ? candidates.find((c) => c.id === value)?.name
                    : <Typography color="text.disabled">Select</Typography>
                }
              >
                {candidates.map((material) => (
                  <MenuItem key={material.id} value={material.id}>
                    {material.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </DialogContent>

      <Divider />

      {/* Single outlined action button, right-aligned, no Cancel */}
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleConfirm}
          variant="outlined"
          disabled={!selectedTargetId || candidates.length === 0}
          sx={{ textTransform: 'none', fontWeight: 600, px: 3 }}
        >
          Link Nodes
        </Button>
      </DialogActions>
    </Dialog>
  );
}
